import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';

import { AddLayerService } from '@connect-services/client/services/add-layer.service';
import { DepositService } from '@connect-services/client/services/deposit.service';
import { LayerFormRecord } from '@connect-services/client/services/layer-creation-form.service';
import { CheckboxComponent, CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import {
    GroupBucket,
    SelectComponent,
    SelectOption,
} from '@connect-services/src/lib/components/select/select.component';
import { SelectByGroupComponent } from '@connect-services/src/lib/components/select-by-group/select-by-group.component';
import { Deposit, DepositFolder } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CheckboxComponent, LoadingComponent, RessourcesModule, SelectByGroupComponent, SelectComponent],
    selector: 'app-select-deposit-step',
    styleUrl: './select-deposit-step.component.scss',
    templateUrl: './select-deposit-step.component.html',
})
export class SelectDepositStepComponent {
    public readonly allDepositsByFolder = signal<Record<string, Deposit[]> | undefined>(undefined);
    public readonly isDepositLoading = signal<boolean>(false);

    protected readonly addLayerService = inject(AddLayerService);
    protected readonly CheckboxState = CheckboxState;
    protected readonly depositDefaultValue = computed(() =>
        this.addLayerService.getDefaultValue(
            this.addLayerService.depositOptions(),
            this.addLayerService.selectedDeposit(),
        ),
    );
    protected readonly depositFolderDefaultValue = computed(() =>
        this.addLayerService.getDefaultValue(
            this.addLayerService.depositFolderOptions(),
            this.addLayerService.selectedDepositFolder(),
        ),
    );
    protected readonly optionsByGroup = signal<GroupBucket<DepositFolder> | null>(null);

    private depositService = inject(DepositService);

    public constructor() {
        effect(() => {
            if (!this.addLayerService.depositFolderOptions() && !this.addLayerService.selectedDepositFolder()) {
                return;
            }

            this.optionsByGroup.set(this.addLayerService.groupDepositsByStandard());
        });
    }

    public selectDeposit(depositId: string[]): void {
        this.addLayerService.selectedDeposit.set(depositId[0]);
        this.addLayerService.isLockVersion.set(false);

        const selectedDeposit = this.allDepositsByFolder()?.[this.addLayerService.selectedDepositFolder()!.id]?.find(
            (deposit) => deposit.id === depositId[0],
        );

        if (!selectedDeposit) {
            return;
        }

        this.addLayerService.selectedDepositVersionAndName.set(`${selectedDeposit.name} _ ${selectedDeposit.version}`);
        this.addLayerService.depositStandard.set(selectedDeposit?.standard ?? undefined);
    }

    public async selectDepositFolder(depositFolder: DepositFolder[]): Promise<void> {
        this.addLayerService.selectedDepositFolder.set(depositFolder[0]);
        this.addLayerService.selectedDeposit.set(undefined);
        this.addLayerService.depositStandard.set(undefined);

        if (!depositFolder.length) {
            return;
        }

        if (!this.allDepositsByFolder()?.[depositFolder[0].id]?.length) {
            this.isDepositLoading.set(true);
            this.allDepositsByFolder.set({
                ...this.allDepositsByFolder(),
                [depositFolder[0].id]: await this.depositService.getAllDeposit(depositFolder[0].id, true),
            });
            this.isDepositLoading.set(false);
        }

        this.initSelectDepositOptions();
    }

    public toggleLockVersion(): void {
        const nextValue = !this.addLayerService.isLockVersion();

        this.addLayerService.isLockVersion.set(nextValue);

        const layerFormRecord: LayerFormRecord | undefined = this.addLayerService.layerFormRecord;

        if (!layerFormRecord) {
            return;
        }

        Object.keys(layerFormRecord.controls).forEach((id: string) => {
            layerFormRecord.controls[id].controls.versionLock.setValue(nextValue);
        });
    }

    private initSelectDepositOptions(): void {
        const hasSelectedDepositFolder = !!this.addLayerService.selectedDepositFolder();
        const folderHasDeposit =
            !!this.allDepositsByFolder()?.[this.addLayerService.selectedDepositFolder()!.id].length;
        const hasDeposits = hasSelectedDepositFolder && folderHasDeposit;

        if (!hasDeposits) {
            this.addLayerService.depositOptions.set([]);
            this.addLayerService.depositFolderHasDeposits.set(false);

            return;
        }

        this.addLayerService.depositFolderHasDeposits.set(true);
        this.addLayerService.depositOptions.set(
            this.allDepositsByFolder()![this.addLayerService.selectedDepositFolder()!.id].reduce(
                (acc: SelectOption<string>[], deposit) => {
                    return [
                        ...acc,
                        {
                            label: `${deposit.version}-${deposit.name}`,
                            state: CheckboxState.UNCHECKED,
                            value: deposit.id,
                        },
                    ];
                },
                [],
            ),
        );
    }
}
